if (!customElements.get('roto-world-signup')) {
  customElements.define(
    'roto-world-signup',
    class RotoWorldSignup extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.roto-world-signup__form');
        this.input = this.querySelector('.roto-world-signup__input');
        this.button = this.querySelector('.roto-world-signup__button');
        this.success = this.querySelector('.roto-world-signup__success');
        if (!this.form || !this.input || !this.button) return;

        this.keepInputVisible = false;
        this.visibilityRaf = 0;

        this.onSubmit = this.onSubmit.bind(this);
        this.onInputFocus = this.onInputFocus.bind(this);
        this.onInputBlur = this.onInputBlur.bind(this);
        this.onVisualViewportChange = this.onVisualViewportChange.bind(this);

        this.form.addEventListener('submit', this.onSubmit);
        this.input.addEventListener('focus', this.onInputFocus);
        this.input.addEventListener('blur', this.onInputBlur);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.input?.removeEventListener('focus', this.onInputFocus);
        this.input?.removeEventListener('blur', this.onInputBlur);
        this.detachVisualViewportListeners();
        if (this.visibilityRaf) cancelAnimationFrame(this.visibilityRaf);
        this.clearVisibilityTimers();
      }

      onInputFocus() {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        this.keepInputVisible = true;
        this.attachVisualViewportListeners();
        this.scheduleScrollIntoView();
      }

      onInputBlur() {
        this.keepInputVisible = false;
        this.detachVisualViewportListeners();
        this.clearVisibilityTimers();
      }

      attachVisualViewportListeners() {
        if (!window.visualViewport || this.visualViewportBound) return;
        window.visualViewport.addEventListener('resize', this.onVisualViewportChange);
        window.visualViewport.addEventListener('scroll', this.onVisualViewportChange);
        this.visualViewportBound = true;
      }

      detachVisualViewportListeners() {
        if (!window.visualViewport || !this.visualViewportBound) return;
        window.visualViewport.removeEventListener('resize', this.onVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', this.onVisualViewportChange);
        this.visualViewportBound = false;
      }

      onVisualViewportChange() {
        if (!this.keepInputVisible) return;
        if (this.visibilityRaf) cancelAnimationFrame(this.visibilityRaf);
        this.visibilityRaf = requestAnimationFrame(() => {
          this.visibilityRaf = 0;
          this.scrollInputIntoView();
        });
      }

      scheduleScrollIntoView() {
        this.clearVisibilityTimers();
        // iOS keyboard animates in; retry while the visual viewport settles.
        this.visibilityTimers = [50, 150, 350].map((delay) =>
          setTimeout(() => {
            if (!this.keepInputVisible) return;
            this.scrollInputIntoView();
          }, delay)
        );
      }

      clearVisibilityTimers() {
        this.visibilityTimers?.forEach((timer) => clearTimeout(timer));
        this.visibilityTimers = [];
      }

      scrollInputIntoView() {
        if (!this.input || document.activeElement !== this.input) return;

        const target = this.form || this.input;
        const rect = target.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const padding = 16;
        const visibleTop = padding;
        const visibleBottom = viewportHeight - padding;

        if (rect.top >= visibleTop && rect.bottom <= visibleBottom) return;

        // Prefer keeping the field in the upper half of the remaining viewport.
        const idealTop = Math.max(padding, viewportHeight * 0.28);
        const delta = rect.top - idealTop;

        if (Math.abs(delta) < 4) return;

        window.scrollBy({
          top: delta,
          left: 0,
          behavior: 'smooth',
        });
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.button.disabled) return;

        const email = this.input.value.trim();
        this.input.value = email;
        if (!email || !this.input.checkValidity()) {
          this.input.reportValidity();
          return;
        }

        const companyId = this.dataset.companyId;
        const listId = this.dataset.listId;
        if (!companyId || !listId) return;

        this.button.disabled = true;

        const headers = {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: '2025-10-15',
        };

        const profileBody = JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
            },
          },
        });

        const subscriptionBody = JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Roto World signup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        });

        Promise.all([
          fetch(`https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(companyId)}`, {
            method: 'POST',
            headers,
            body: profileBody,
          }),
          fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
            method: 'POST',
            headers,
            body: subscriptionBody,
          }),
        ])
          .then((responses) => {
            const succeeded = responses.every((response) => response.ok);
            if (!succeeded) throw new Error('Klaviyo request failed');

            this.form.hidden = true;
            const signupText = this.querySelector('.roto-world-signup__text');
            if (signupText) signupText.hidden = true;
            if (this.success) this.success.hidden = false;
          })
          .catch(() => {
            this.button.disabled = false;
          });
      }
    }
  );
}
